import { useState, useEffect } from "react";
import { API } from "../App";
import Layout from "../components/Layout";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Bell, Send, Mail, Users } from "lucide-react";

const AdminNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  
  const [form, setForm] = useState({
    title: '',
    message: '',
    type: 'general',
    target_users: [],
    send_email: false
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { withCredentials: true })
      ]);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleSendNotification = async () => {
    if (!form.title || !form.message) {
      toast.error("Please fill in title and message");
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API}/notifications`, form, { withCredentials: true });
      toast.success("Notification sent successfully!");
      setForm({
        title: '',
        message: '',
        type: 'general',
        target_users: [],
        send_email: false
      });
    } catch (error) {
      toast.error("Failed to send notification");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-700"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in max-w-3xl" data-testid="admin-notifications">
        {/* Header */}
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-stone-900" style={{ fontFamily: 'Outfit' }}>
            Send Notifications
          </h1>
          <p className="text-stone-600 mt-1">Notify students about updates and announcements</p>
        </div>

        {/* Create Notification */}
        <Card className="bg-white rounded-xl border border-stone-100">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Bell className="h-5 w-5 text-violet-700" />
              New Notification
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label>Title *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Notification title"
                className="mt-1"
                data-testid="notification-title-input"
              />
            </div>

            <div>
              <Label>Message *</Label>
              <Textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Write your notification message..."
                rows={4}
                className="mt-1"
                data-testid="notification-message-input"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Notification Type</Label>
                <Select 
                  value={form.type} 
                  onValueChange={(v) => setForm({ ...form, type: v })}
                >
                  <SelectTrigger className="mt-1" data-testid="notification-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="assignment">Assignment</SelectItem>
                    <SelectItem value="course">Course Update</SelectItem>
                    <SelectItem value="alert">Alert</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Target Audience</Label>
                <Select 
                  value={form.target_users.length === 0 ? "all" : "specific"}
                  onValueChange={(v) => setForm({ ...form, target_users: v === "all" ? [] : form.target_users })}
                >
                  <SelectTrigger className="mt-1" data-testid="notification-target-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Students</SelectItem>
                    <SelectItem value="specific">Specific Users (Coming Soon)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-stone-50 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                  <Mail className="h-5 w-5 text-violet-700" />
                </div>
                <div>
                  <p className="font-medium text-stone-900">Send Email Notification</p>
                  <p className="text-sm text-stone-500">Also send this notification via email</p>
                </div>
              </div>
              <Switch
                checked={form.send_email}
                onCheckedChange={(checked) => setForm({ ...form, send_email: checked })}
                data-testid="send-email-switch"
              />
            </div>

            <Button 
              className="w-full bg-violet-700 hover:bg-violet-800 h-12 rounded-xl"
              onClick={handleSendNotification}
              disabled={sending}
              data-testid="send-notification-btn"
            >
              {sending ? (
                "Sending..."
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Notification
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="bg-violet-50 border-violet-100 rounded-xl">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Users className="h-6 w-6 text-violet-700" />
              </div>
              <div>
                <h3 className="font-semibold text-stone-900 mb-1" style={{ fontFamily: 'Outfit' }}>
                  Notification Recipients
                </h3>
                <p className="text-stone-600 text-sm">
                  Notifications will be sent to all {users.filter(u => u.role === 'student').length} students 
                  in the platform. If email notification is enabled, students will also receive an email 
                  (limited to 10 emails in testing mode).
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AdminNotifications;
